import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';

import type { GeneralActivityGrantImpact } from '@time-tracking/shared';

import { AlertTriangleIcon } from '../../../components/icons/AlertTriangleIcon.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';
import {
  joinNames,
  keepsItLabel,
  losingCount,
  NAME_CAP,
  removedByLabel,
  rowStaysLabel,
  teamWithLead,
} from './grant-copy.js';
import { CheckIcon } from './icons.js';

/**
 * The confirm dialogs of General activities: the unassign dialog (a person
 * untick, or a team off) and the team-on override confirmation. Handoff:
 * design_handoff_general_activities/src/general-activities.jsx
 * (`UnassignDialog`, `TurnOnDialog`), screenshots 02–05.
 */

/** Shown in place of the panels when a confirm came back "impact changed". */
const IMPACT_CHANGED_NOTE =
  'Who this affects changed while this was open. Check the list again before you confirm.';

interface GrantDialogProps {
  title: string;
  sub: string;
  /** The impact area; announced politely as it fills in or changes. */
  children: ReactNode;
  primaryLabel: string;
  /** False while the impact is still being checked, or when there is nothing to confirm. */
  canConfirm: boolean;
  /** The confirmed write is in flight: neither button acts, Esc and the backdrop do nothing. */
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * A one-line summary for screen readers, announced whenever it changes after
   * the dialog opens (the body itself is the dialog's description, read on
   * open). Saving, a preview arriving, and "impact changed" each change it.
   */
  announcement: string;
  /** Owned by the caller so it can put focus back on Cancel (e.g. after "Try again"). */
  cancelRef: RefObject<HTMLButtonElement | null>;
}

/**
 * The shell all three share: `alertdialog`, modal, focus trapped, initial focus
 * on Cancel, Esc and a backdrop click cancel.
 *
 * While saving both buttons are `aria-disabled`, not `disabled`: a natively
 * disabled button drops the focus it holds to `<body>`, and the confirm is
 * exactly where focus is while its write is in flight — and where it must still
 * be if the server answers "impact changed" and asks again.
 */
function GrantDialog({
  title,
  sub,
  children,
  primaryLabel,
  canConfirm,
  saving,
  onConfirm,
  onCancel,
  announcement,
  cancelRef,
}: GrantDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const subId = useId();
  const bodyId = useId();
  useFocusTrap(dialogRef, true, cancelRef);

  // The live region mounts empty and only ever receives changes after open:
  // what is there at open is read as the description, not announced twice.
  const opened = useRef(announcement);
  const [live, setLive] = useState('');
  useEffect(() => {
    if (announcement !== opened.current) setLive(announcement);
  }, [announcement]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, saving]);

  return (
    <div
      className="dialog-backdrop ga-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="ga-dlg"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={`${subId} ${bodyId}`}
      >
        <div className="ga-dlg__head">
          <h2 id={titleId} className="ga-dlg__title">
            {title}
          </h2>
          <p id={subId} className="ga-dlg__sub">
            {sub}
          </p>
        </div>
        <div id={bodyId} className="ga-dlg__body">
          {children}
        </div>
        <div className="sr-only" role="status">
          {live}
        </div>
        <div className="ga-dlg__footer">
          <button
            ref={cancelRef}
            type="button"
            className="btn btn--secondary"
            aria-disabled={saving || undefined}
            onClick={() => {
              if (!saving) onCancel();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canConfirm && !saving}
            aria-disabled={saving || undefined}
            aria-busy={saving || undefined}
            onClick={() => {
              if (canConfirm && !saving) onConfirm();
            }}
          >
            {saving ? 'Saving…' : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Checking() {
  return (
    <div className="ga-impact">
      <span className="ga-spin" aria-hidden="true" />
      <span>Checking who this affects…</span>
    </div>
  );
}

function ChangedNote() {
  return <p className="ga-dlg__changed">{IMPACT_CHANGED_NOTE}</p>;
}

function CheckFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="ga-impact warn">
      <span className="ic ic--warn">
        <AlertTriangleIcon className="ga-ico-14" />
      </span>
      <div>
        Couldn’t check who this affects. Nothing was changed.{' '}
        <button type="button" className="ga-dlg__retry" onClick={onRetry}>
          Try again
        </button>
      </div>
    </div>
  );
}

/** The live-region line for a loaded preview: who loses it, whose rows stay. */
function impactSummary(preview: GeneralActivityGrantImpact): string {
  const n = losingCount(preview);
  if (n === 0) return 'Nobody loses it.';
  const k = preview.keptWithHours.length;
  const loses = `${n} ${n === 1 ? 'person loses' : 'people lose'} it`;
  return k === 0
    ? `${loses}.`
    : `${loses}; ${k} ${k === 1 ? 'keeps their row' : 'keep their rows'} with hours or a note.`;
}

/** "{a}; {b}; {c} and N more" — prose lists capped at {@link NAME_CAP}. */
function cappedProse(items: readonly string[]): string {
  const shown = items.slice(0, NAME_CAP).join('; ');
  const more = items.length - NAME_CAP;
  return more > 0 ? `${shown} and ${more} more` : shown;
}

interface UnassignBase {
  activityName: string;
  /** Null while "Checking who this affects…". */
  preview: GeneralActivityGrantImpact | null;
  /** The preview could not be loaded. */
  failed: boolean;
  /** A confirm came back "impact changed" and `preview` is the fresh one. */
  changed: boolean;
  saving: boolean;
  onRetry: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export type UnassignDialogProps = UnassignBase &
  (
    | {
        kind: 'person';
        personName: string;
        /** The row's own team, left out of "Also on another team". Null for No team. */
        ownTeamId: string | null;
      }
    | { kind: 'team'; teamName: string }
  );

/**
 * `Remove "{Activity}" from {Name}?` / `… from everyone on {Team}?` — who loses
 * it, whose rows stay, and (person) the other teams it also leaves or (team)
 * who keeps it and why.
 */
export function UnassignDialog(props: UnassignDialogProps) {
  const { activityName, preview, failed, changed, saving, onRetry, onConfirm, onCancel } = props;
  const isTeam = props.kind === 'team';
  const title = isTeam
    ? `Remove “${activityName}” from everyone on ${props.teamName}?`
    : `Remove “${activityName}” from ${props.personName}?`;

  const n = preview ? losingCount(preview) : 0;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const announcement = saving
    ? 'Saving…'
    : failed
      ? 'Couldn’t check who this affects. Nothing was changed.'
      : !preview
        ? 'Checking who this affects…'
        : `${changed ? `${IMPACT_CHANGED_NOTE} ` : ''}${impactSummary(preview)}`;
  const primaryLabel = isTeam
    ? n > 0
      ? `Remove from ${n} ${n === 1 ? 'person' : 'people'}`
      : `Turn off for ${props.teamName}`
    : `Remove ${activityName}`;
  // A person untick with nobody losing it has nothing to confirm; a team off
  // with nobody losing it still turns the team setting off.
  const canConfirm = preview !== null && (isTeam || n > 0);

  let body: ReactNode;
  if (failed) {
    body = (
      <CheckFailed
        onRetry={() => {
          // The button is replaced by "Checking…": park focus on Cancel
          // rather than let it fall to <body> inside the modal (2.4.3).
          cancelRef.current?.focus();
          onRetry();
        }}
      />
    );
  } else if (!preview) {
    body = <Checking />;
  } else {
    const kept = preview.keptWithHours;
    const others = preview.lost.length;
    const alsoOn =
      props.kind === 'person'
        ? preview.alsoOnTeams.filter((t) => t.teamId !== props.ownTeamId)
        : [];
    body = (
      <>
        {changed && <ChangedNote />}
        {isTeam && n === 0 && (
          <div className="ga-impact ok">
            <span className="ic ic--ok">
              <CheckIcon />
            </span>
            <span>
              <b>Nobody loses it.</b> Everyone on {props.teamName} also has it another way.
            </span>
          </div>
        )}
        {n > 0 && kept.length === 0 && (
          <div className="ga-impact ok">
            <span className="ic ic--ok">
              <CheckIcon />
            </span>
            <span>
              <b>Nobody has hours or a note against it.</b>{' '}
              {n === 1 ? 'The row leaves their' : `The row leaves ${n} people’s`} timesheets not yet
              submitted.
            </span>
          </div>
        )}
        {kept.length > 0 && (
          <div className="ga-impact warn">
            <span className="ic ic--warn">
              <AlertTriangleIcon className="ga-ico-14" />
            </span>
            <div className="ga-impact__main">
              <b>
                {kept.length} {kept.length === 1 ? 'person has' : 'people have'} hours this period
              </b>{' '}
              — their rows stay but won’t take new hours.
              {/* The list scrolls past ~5 rows, so its wrapper takes focus
                  (keyboard scrolling) and carries the name; the list keeps
                  its list semantics. */}
              <div
                className="ga-people-scroll"
                role="region"
                tabIndex={0}
                aria-label="People whose rows stay"
              >
                <ul className="ga-people">
                  {kept.map((p) => (
                    <li key={p.userId}>
                      <span>{p.displayName}</span>
                      <span className="ga-people__stays">{rowStaysLabel(p)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {others > 0 && (
                <div className="ga-impact__others">
                  {others === 1 ? '1 other person loses' : `${others} others lose`} the row from
                  timesheets not yet submitted.
                </div>
              )}
            </div>
          </div>
        )}
        {isTeam && preview.keepsIt.length > 0 && (
          <div className="ga-cross">
            <b>Keeps it.</b> {cappedProse(preview.keepsIt.map(keepsItLabel))}.
          </div>
        )}
        {props.kind === 'person' && alsoOn.length > 0 && (
          <div className="ga-cross">
            <b>Also on another team.</b> {props.personName} is also on{' '}
            {joinNames(alsoOn.slice(0, NAME_CAP).map(teamWithLead))}
            {alsoOn.length > NAME_CAP ? ` and ${alsoOn.length - NAME_CAP} more` : ''}. Removing it
            here removes it from their other team too.
          </div>
        )}
      </>
    );
  }

  return (
    <GrantDialog
      announcement={announcement}
      cancelRef={cancelRef}
      title={title}
      sub="Timesheet lines never disappear while they hold hours."
      primaryLabel={primaryLabel}
      canConfirm={canConfirm}
      saving={saving}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {body}
    </GrantDialog>
  );
}

export interface TurnOnDialogProps {
  activityName: string;
  teamName: string;
  /** The `grant_impact_changed` preview whose `overridden` list prompted this. */
  preview: GeneralActivityGrantImpact;
  changed: boolean;
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * `Turn on {Activity} for everyone on {Team}?` — shown only when turning the
 * team on would clear removals another lead made.
 */
export function TurnOnDialog({
  activityName,
  teamName,
  preview,
  changed,
  saving,
  onConfirm,
  onCancel,
}: TurnOnDialogProps) {
  const list = preview.overridden;
  const more = list.length - NAME_CAP;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const turnOnAnnouncement = saving
    ? 'Saving…'
    : changed
      ? `${IMPACT_CHANGED_NOTE} ${list.length === 1 ? '1 person' : `${list.length} people`} another lead removed would get it back.`
      : '';
  return (
    <GrantDialog
      announcement={turnOnAnnouncement}
      cancelRef={cancelRef}
      title={`Turn on ${activityName} for everyone on ${teamName}?`}
      sub={`This gives it back to ${list.length === 1 ? 'someone another lead' : 'people other leads'} removed.`}
      primaryLabel="Turn on for everyone"
      canConfirm
      saving={saving}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {changed && <ChangedNote />}
      <ul className="ga-people ga-people--flush">
        {list.slice(0, NAME_CAP).map((p) => (
          <li key={p.userId}>
            <span>
              {p.displayName} — {removedByLabel(p)}
            </span>
          </li>
        ))}
        {more > 0 && (
          <li>
            <span className="ga-people__more">and {more} more</span>
          </li>
        )}
      </ul>
    </GrantDialog>
  );
}
